import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { Dayjs } from 'dayjs';
import { theme } from 'antd';
import CustomTable from '../CustomTable';
import TextBox from 'components/TextBox';
import NestedTable from '../NestedTable';
import CustomSelect from 'components/CustomSelect';
import additionalInfoSaveKeys from 'constants/additionalInfoSaveKeys';
import Helpers from 'utils/Helpers';
import CustomDatePicker from 'components/CustomDatePicker';
import AuditsSelectBox from 'components/AuditsSelectBox';
import { RootState } from 'store/configureStore';
import { gapsOp } from 'store/ducks/Gaps';
import {
  IAdditionalInfo,
  IAdditionalInfoData,
  IAudits
} from 'store/ducks/Gaps/types';
import { YesNo } from 'store/ducks/globalTypes';
import { globalOp } from 'store/ducks/Global';
import { complianceEstOp } from 'store/ducks/ComplienceEst';
import { GapAdditionalInfoColumns } from 'constants/tableColumns';
import * as Styled from './AdditionalInfoTable.styled';

const { useToken } = theme;

const AdditionalInfoTable: React.FC = React.memo(() => {
  const dispatch = useDispatch();
  const { token } = useToken();

  const [prioritization, setPrioritization] = useState<string[]>([]);
  const [gapSource, setGapSource] = useState<string[]>([]);
  const [ownersList, setOwnersList] = useState<string[]>([]);
  const gapAdditionalInfoColumns = GapAdditionalInfoColumns();

  const {
    lang,
    drawTable,
    additionalInfo,
    saveClicked,
    infoSave,
    infoUpdated,
    loading,
    scheme
  } = useSelector((state: RootState) => ({
    lang: state.global.lang,
    drawTable: state.complianceEst.drawTable,
    loading: state.gaps.loading,
    additionalInfo: state.gaps.additionalInfo,
    saveClicked: state.complianceEst.saveClicked,
    infoSave: state.gaps.infoSave,
    infoUpdated: state.gaps.infoUpdated,
    scheme: state.global.scheme
  }));

  useEffect(() => {
    if (!Helpers.isObjEmpty(infoSave) && saveClicked) {
      // Execute save immediately without delay
      gapsOp
        .saveGapsInfo(Object.values(infoSave), lang)
        .then(() => {
          dispatch(complianceEstOp.setSaveClicked(false));
          dispatch(gapsOp.setInfoSaveData({}));
        })
        .catch((error) => {
          dispatch(complianceEstOp.setSaveClicked(false)); // Reset even on error
        });
    }
  }, [saveClicked, infoSave, lang, dispatch]);

  const handleSelectChange = (
    value: string,
    type: string,
    data: IAdditionalInfo
  ) => {
    const updatedData = () => {
      const newState = { ...infoSave };
      const gapId = data.gap_id as number;

      // Use existing saved data if available, otherwise use original data
      const existingData = newState[gapId] || {};

      const newData = additionalInfoSaveKeys.reduce((obj, item) => {
        // Priority: 1. Existing saved value, 2. Original data value
        const existingValue = existingData[item as keyof typeof existingData];
        const originalValue = data[item as keyof typeof data];
        return Object.assign(obj, {
          [item]: existingValue !== undefined ? existingValue : originalValue
        });
      }, {});

      newState[gapId] = { ...newData, [type]: value };
      return newState;
    };

    dispatch(gapsOp.setInfoSaveData(updatedData()));
  };

  const dataSource =
    additionalInfo && additionalInfo.length ? additionalInfo.flat() : [];

  // Check if both gaps and audits are present
  const hasBothGapsAudits = dataSource.some(
    (item: IAdditionalInfoData) => item.AUDITS && item.AUDITS.length > 0
  );

  const expandedRow = useCallback(
    (record: IAdditionalInfoData) => {
      if (!record) return null;

      // Check if this specific record has both gaps and audits
      const recordHasBothGapsAudits = record.AUDITS && record.AUDITS.length > 0;

      const nestedTableColumns = [
        {
          title: 'Gaps',
          dataIndex: 'gap_desc',
          key: 'gap_desc',
          width: 335,
          render: (desc: string) => <TextBox width={335}>{desc}</TextBox>
        },
        {
          title: 'Completion Due Date',
          dataIndex: 'gap_due_date',
          key: 'gap_due_date',
          width: 300,
          render: (dueDate: string, dataObj: IAdditionalInfo) => (
            <CustomDatePicker
              onChange={(date: Dayjs, dateString: string) =>
                handleSelectChange(dateString, 'gap_due_date', dataObj)
              }
              defaultValue={dueDate}
            />
          )
        },
        {
          title: 'Due Date Audit',
          dataIndex: 'AUDITS',
          key: 'AUDITS_DUE_DATE',
          width: 195,
          render: (audits: IAudits[]) =>
            audits && audits.length ? (
              <AuditsSelectBox audits={audits} valueKey='afg_due_date' dateEl />
            ) : (
              <Styled.StyledTextBox width={195} />
            )
        },
        {
          title: 'Gap Prioritization',
          dataIndex: 'gap_pri_ref',
          key: 'gap_pri_ref',
          width: 120,
          render: (prio: string, dataObj: IAdditionalInfo) => (
            <CustomSelect
              size='md'
              options={Helpers.convertToSelectOptions(prioritization)}
              defaultValue={prio}
              onChange={(value: string) =>
                handleSelectChange(value, 'gap_pri_ref', dataObj)
              }
            />
          )
        },
        {
          title: 'Audit Finding Prioritization',
          dataIndex: 'AUDITS',
          key: 'AUDITS_PRIORITY',
          width: 195,
          render: (audits: IAudits[]) =>
            audits && audits.length ? (
              <AuditsSelectBox audits={audits} valueKey='afg_prio_short_name' />
            ) : (
              <Styled.StyledTextBox width={195} />
            )
        },
        {
          title: 'Gap Source',
          dataIndex: 'gap_src_ref',
          key: 'gap_src_ref',
          width: 120,
          render: (gap_source: string, dataObj: IAdditionalInfo) => (
            <CustomSelect
              size='md'
              options={Helpers.convertToSelectOptions(gapSource)}
              defaultValue={gap_source}
              onChange={(value: string) =>
                handleSelectChange(value, 'gap_src_ref', dataObj)
              }
            />
          )
        },
        {
          title: 'Source Audit',
          dataIndex: 'AUDITS',
          key: 'AUDITS_SOURCE',
          width: 195,
          render: (audits: IAudits[]) =>
            audits && audits.length ? (
              <AuditsSelectBox audits={audits} valueKey='ads_short_name' />
            ) : (
              <Styled.StyledTextBox width={195} />
            )
        },
        {
          title: 'gap owner notification',
          dataIndex: 'gap_owner_notified',
          key: 'gap_owner_notified',
          width: 100,
          render: (ownerNotified: string) => (
            <Styled.StyledTag
              color={ownerNotified === YesNo.YES ? 'green' : 'red'}
            >
              {ownerNotified === YesNo.YES ? 'Yes' : 'No'}
            </Styled.StyledTag>
          )
        },
        {
          title: 'Gap Owner',
          dataIndex: 'gap_owner_ref',
          key: 'gap_owner_ref',
          width: 150,
          render: (gap_owner_ref: string, dataObj: IAdditionalInfo) => (
            <CustomSelect
              size='lg'
              options={Helpers.convertToSelectOptions(ownersList)}
              defaultValue={gap_owner_ref}
              onChange={(value: string) =>
                handleSelectChange(value, 'gap_owner_ref', dataObj)
              }
            />
          )
        },
        {
          title: 'Audit Owner',
          dataIndex: 'AUDITS',
          key: 'AUDITS_OWNER',
          width: 195,
          render: (audits: IAudits[]) =>
            audits && audits.length ? (
              <AuditsSelectBox audits={audits} valueKey='afg_owner_ref' />
            ) : (
              <Styled.StyledTextBox width={195} />
            )
        }
      ];

      return (
        <Styled.StyledNestedTable
          className={`nested-table ${
            recordHasBothGapsAudits ? 'has-both-data' : ''
          }`}
          columns={nestedTableColumns}
          key={dataSource[0]?.gap_id}
          dataSource={[{ ...record, key: record.gap_id }]}
          pagination={false}
          token={token}
        />
      );
    },
    [dataSource, prioritization, gapSource, ownersList]
  );

  useEffect(() => {
    const isEmpty = Helpers.isObjEmpty(infoSave);
    // Update save button state immediately
    dispatch(complianceEstOp.setSaveDisabled(isEmpty));
  }, [infoSave, dispatch]);

  useEffect(() => {
    gapsOp.fetchPrioritization(lang).then((response) => {
      const sorted = [...(response?.prioritization || [])].sort((a, b) =>
        a.localeCompare(b)
      );
      setPrioritization(sorted);
    });

    gapsOp.fetchGapSource(lang).then((response) => {
      const sorted = [...(response?.gap_sources || [])].sort((a, b) =>
        a.localeCompare(b)
      );
      setGapSource(sorted);
    });

    globalOp.fetchOwnersList().then((response: string[]) => {
      const sorted = [...(response || [])].sort((a, b) => a.localeCompare(b));
      setOwnersList(sorted);
    });
  }, []);

  return (
    <>
      <Styled.StyledCustomTable
        drawTable
        token={token}
        hasBothGapsAudits={hasBothGapsAudits}
        className={hasBothGapsAudits ? 'has-both-gaps-audits' : ''}
        columns={gapAdditionalInfoColumns}
        expandable={{
          expandedRowKeys: dataSource.map(
            (item: IAdditionalInfoData) => item.gap_id
          ),
          expandedRowRender: (record: IAdditionalInfoData) =>
            expandedRow(record)
        }}
        dataSource={dataSource.map((item: IAdditionalInfoData) => ({
          ...item,
          key: item.gap_id
        }))}
        itemsCount={dataSource.length}
        loading={loading}
      />
    </>
  );
});

export default AdditionalInfoTable;
